/**
 * App Entry Point
 *
 * Validates Auth0 config (ADR-CEN-14 fast-fail) and renders Auth0Provider + Router.
 */

import { Auth0Provider } from '@auth0/auth0-react'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { validateAuthConfig, AuthConfigError } from '@lib/auth'
import { routeTree } from './routeTree.gen'

const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

/** Validate config at module load — fast-fail per ADR-CEN-14 */
let authConfig: ReturnType<typeof validateAuthConfig> | null = null
let configError: AuthConfigError | null = null

try {
  authConfig = validateAuthConfig()
} catch (e) {
  if (e instanceof AuthConfigError) {
    configError = e
  } else {
    throw e
  }
}

export function App() {
  if (configError) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="text-center max-w-lg">
          <h1 className="font-mono text-red-500 text-xl font-bold mb-4">
            Configuration Error
          </h1>
          <p className="font-mono text-neutral-400 text-sm mb-4">
            {configError.message}
          </p>
          <p className="font-mono text-neutral-500 text-xs">
            Missing: {configError.missing.join(', ')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <Auth0Provider
      domain={authConfig!.domain}
      clientId={authConfig!.clientId}
      useRefreshTokens={true}
      cacheLocation="memory"
      authorizationParams={{
        redirect_uri: authConfig!.redirectUri,
        audience: authConfig!.audience || undefined,
        organization: authConfig!.organization || undefined,
      }}
    >
      <RouterProvider router={router} />
    </Auth0Provider>
  )
}
